import React from "react";
import {
  isContainerDirectiveEnd,
  parseContainerDirectiveStart,
  parseDirectiveLine,
} from "@/lib/MarkdownDirective";
import { extractMarkdownHeadings } from "@/lib/MarkdownHeadings";
import {
  parseInlineMarkdown,
  type TextDirectiveRenderer,
} from "@/lib/MarkdownInline";
import {
  isTableStart,
  parseMarkdownTable,
  type MarkdownTable,
} from "@/lib/MarkdownTable";

// セキュリティ方針:
// - 本コンポーネントは Markdown を HTML 文字列へ変換せず、React 要素へ直接マッピングする。
// - `dangerouslySetInnerHTML` は使用せず、テキストは React の標準エスケープに委ねる。
// - インラインリンク（`[text](url)`）の安全性検証は `@/lib/MarkdownInline` に委譲する。
// - 将来的に HTML 埋め込みを許可する場合は、DOMPurify 等での明示的なサニタイズを必須とする。

export interface MarkdownStyles {
  h1: string;
  h2: string;
  h3: string;
  p: string;
  ul: string;
  li: string;
  blockquote: string;
  blockquoteParagraph: string;
  a: string;
  strong: string;
  sup: string;
  supLink: string;
  tableWrapper: string;
  table: string;
  th: string;
  tbody: string;
  td: string;
  details: string;
  summary: string;
  detailsBody: string;
  message: string;
  messageAlert: string;
  messageIcon: string;
  messageAlertIcon: string;
  messageBody: string;
}

export const DEFAULT_MARKDOWN_STYLES: MarkdownStyles = {
  h1: "text-2xl font-bold py-2 text-primary-500",
  h2: "text-xl font-bold py-2 text-primary-500",
  h3: "text-base font-bold py-2 text-primary-500",
  p: "text-xs text-primary-700",
  ul: "list-disc pl-5",
  li: "text-xs text-primary-700",
  blockquote: "text-xs text-primary-700",
  blockquoteParagraph: "",
  a: "text-primary-500 underline hover:text-primary-700",
  strong: "font-bold",
  sup: "ml-0.5 text-[0.7em] align-super",
  supLink: "text-primary-500 hover:text-primary-700",
  tableWrapper: "overflow-x-auto",
  table: "w-full text-xs",
  th: "border px-2 py-1 font-bold",
  tbody: "",
  td: "border px-2 py-1",
  details: "",
  summary: "cursor-pointer",
  detailsBody: "",
  message: "",
  messageAlert: "",
  messageIcon: "",
  messageAlertIcon: "",
  messageBody: "",
};

// Zenn互換のメッセージ（`:::message` 〜 `:::`）。ラベルが `alert` なら注意として扱う。
type MessageVariant = "info" | "alert";

function parseMessageVariant(label: string): MessageVariant | null {
  switch (label) {
    case "":
      return "info";
    case "alert":
      return "alert";
    default:
      return null;
  }
}

const MESSAGE_LABELS: Record<MessageVariant, string> = {
  info: "補足",
  alert: "注意",
};

export type MarkdownDirectiveRenderer = (
  attributes: Record<string, string>,
  key: string,
) => React.ReactNode;

export const MarkdownRenderer: React.FC<{
  content: string;
  styles?: MarkdownStyles;
  directives?: Record<string, MarkdownDirectiveRenderer>;
  textDirectives?: Record<string, TextDirectiveRenderer>;
}> = ({
  content,
  styles = DEFAULT_MARKDOWN_STYLES,
  directives,
  textDirectives = {},
}) => {
  return <>{parseMarkdown(content, styles, directives, textDirectives)}</>;
};

function parseMarkdown(
  content: string,
  styles: MarkdownStyles,
  directives: Record<string, MarkdownDirectiveRenderer> | undefined,
  textDirectives: Record<string, TextDirectiveRenderer>,
): React.ReactNode[] {
  const lines = content.split(/\r?\n/);
  const headings = extractMarkdownHeadings(content);
  let headingIndex = 0;
  const nextHeadingId = () => headings[headingIndex++]?.id;
  const elements: React.ReactNode[] = [];
  let list: string[] = [];
  let block: string[] = [];

  const flushList = () => {
    if (list.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className={styles.ul}>
          {list.map((item, i) => (
            <li key={i} className={styles.li}>
              {parseInlineMarkdown(item, styles, textDirectives)}
            </li>
          ))}
        </ul>,
      );
      list = [];
    }
  };

  const flushBlock = () => {
    if (block.length > 0) {
      elements.push(
        <blockquote key={`bq-${elements.length}`} className={styles.blockquote}>
          {block.map((txt, i) => (
            <p key={i} className={styles.blockquoteParagraph}>
              {parseInlineMarkdown(txt, styles, textDirectives)}
            </p>
          ))}
        </blockquote>,
      );
      block = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("> ")) {
      block.push(line.slice(2));
      i++;
      continue;
    }
    flushBlock();

    if (line.startsWith("- ")) {
      list.push(line.slice(2));
      i++;
      continue;
    }
    flushList();

    const directive = parseDirectiveLine(line);
    const directiveRenderer =
      directive !== null ? directives?.[directive.name] : undefined;
    if (directive !== null && directiveRenderer !== undefined) {
      elements.push(
        directiveRenderer(directive.attributes, `directive-${elements.length}`),
      );
      i++;
      continue;
    }

    const containerStart = parseContainerDirectiveStart(line);
    const containerEnd = findContainerDirectiveEnd(lines, i + 1);
    const containerBody = lines.slice(i + 1, containerEnd).join("\n");
    const messageVariant =
      containerStart !== null && containerStart.name === "message"
        ? parseMessageVariant(containerStart.label)
        : null;
    if (messageVariant !== null) {
      elements.push(
        <aside
          key={`message-${elements.length}`}
          aria-label={MESSAGE_LABELS[messageVariant]}
          className={
            messageVariant === "alert" ? styles.messageAlert : styles.message
          }
        >
          <span
            aria-hidden="true"
            className={
              messageVariant === "alert"
                ? styles.messageAlertIcon
                : styles.messageIcon
            }
          >
            !
          </span>
          <div className={styles.messageBody}>
            {parseMarkdown(containerBody, styles, directives, textDirectives)}
          </div>
        </aside>,
      );
      i = containerEnd + 1;
      continue;
    }

    if (containerStart !== null && containerStart.name === "details") {
      const summaryText =
        containerStart.label !== "" ? containerStart.label : "詳細";
      elements.push(
        <details key={`details-${elements.length}`} className={styles.details}>
          <summary className={styles.summary}>
            {parseInlineMarkdown(summaryText, styles, textDirectives)}
          </summary>
          <div className={styles.detailsBody}>
            {parseMarkdown(containerBody, styles, directives, textDirectives)}
          </div>
        </details>,
      );
      i = containerEnd + 1;
      continue;
    }

    if (isTableStart(line, lines[i + 1])) {
      let j = i + 2;
      while (j < lines.length && lines[j].startsWith("|")) {
        j++;
      }
      const table = parseMarkdownTable(lines.slice(i, j));
      elements.push(
        renderTable(table, styles, elements.length, textDirectives),
      );
      i = j;
      continue;
    }

    if (line.startsWith("### ")) {
      elements.push(
        <h3
          key={`h3-${elements.length}`}
          id={nextHeadingId()}
          className={styles.h3}
        >
          {parseInlineMarkdown(line.slice(4), styles, textDirectives)}
        </h3>,
      );
    } else if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={`h2-${elements.length}`}
          id={nextHeadingId()}
          className={styles.h2}
        >
          {parseInlineMarkdown(line.slice(3), styles, textDirectives)}
        </h2>,
      );
    } else if (line.startsWith("# ")) {
      elements.push(
        <h1
          key={`h1-${elements.length}`}
          id={nextHeadingId()}
          className={styles.h1}
        >
          {parseInlineMarkdown(line.slice(2), styles, textDirectives)}
        </h1>,
      );
    } else if (line.trim() !== "") {
      elements.push(
        <p key={`p-${elements.length}`} className={styles.p}>
          {parseInlineMarkdown(line, styles, textDirectives)}
        </p>,
      );
    }
    i++;
  }
  flushBlock();
  flushList();
  return elements;
}

function findContainerDirectiveEnd(lines: string[], start: number): number {
  let end = start;
  while (end < lines.length && !isContainerDirectiveEnd(lines[end])) {
    end++;
  }
  return end;
}

function alignClass(alignment: MarkdownTable["alignments"][number]): string {
  switch (alignment) {
    case "right":
      return "text-right";
    case "center":
      return "text-center";
    default:
      return "text-left";
  }
}

function renderTable(
  table: MarkdownTable,
  styles: MarkdownStyles,
  key: number,
  textDirectives: Record<string, TextDirectiveRenderer>,
): React.ReactNode {
  return (
    <div key={`table-${key}`} className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            {table.header.map((cell, i) => (
              <th
                key={i}
                scope="col"
                className={`${styles.th} ${alignClass(table.alignments[i])}`}
              >
                {parseInlineMarkdown(cell, styles, textDirectives)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {table.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td
                  key={c}
                  className={`${styles.td} ${alignClass(table.alignments[c])}`}
                >
                  {parseInlineMarkdown(cell, styles, textDirectives)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
